import { activeClubs, clubsOfFaculty, FACULTIES, type ClubDef } from "@clubap/shared";
import { useEffect, useState } from "react";
import { ClubMark } from "./ClubBadges.js";

interface Props {
  /**
   * De qué facultad son los clubes que se listan. `null` los lista TODOS.
   *
   * No es lo mismo que `clubsOfFaculty(null)`, que trae sólo los transversales: acá
   * `null` significa "el patio de contenedores", el directorio de todo el campus, y un
   * id significa "el vestíbulo de esa facultad".
   */
  facultyId: string | null;
  /** Los clubes a los que ya pertenece quien mira. Se marcan, no se esconden. */
  mine: string[];
  onOpenClub: (clubId: string) => void;
  onFound: () => void;
  onClose: () => void;
}

/**
 * Cuántos clubes se pintan de una.
 *
 * Hay catorce hoy y puede haber quinientos: fundar es de los jugadores. Una lista sin
 * tope funciona perfectamente hasta el día que deja de funcionar, y ese día nadie está
 * mirando. El buscador es la salida normal; esto es el techo por debajo.
 */
const PAGINA = 30;

/** Sin tildes y en minúsculas: buscar "robotica" tiene que encontrar "Robótica". */
function plano(texto: string): string {
  /* NFD parte la letra acentuada en letra + tilde suelta, y `\p{Diacritic}` borra las
     tildes sueltas. Se usa la propiedad Unicode y no el rango U+0300–U+036F escrito a
     mano porque ese rango, tecleado literal, son caracteres invisibles en el editor:
     cualquiera que edite alrededor los borra sin verlos. */
  return texto
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

/**
 * El directorio de sedes: qué clubes hay y cuál abrir.
 *
 * Es la misma pantalla en dos sitios —el patio de contenedores lo enseña entero, el
 * vestíbulo de una facultad enseña el suyo— y por eso la facultad es una prop y no algo
 * que este panel deduzca de dónde está el jugador: quien lo monta ya lo sabe.
 */
export function ClubDirectory({ facultyId, mine, onOpenClub, onFound, onClose }: Props) {
  const [query, setQuery] = useState("");
  const [limite, setLimite] = useState(PAGINA);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /* Volver al techo al cambiar lo que se busca: si no, quien pidió "ver más" en una
     búsqueda ancha se lleva ese techo alto a la siguiente sin haberlo pedido. */
  useEffect(() => {
    setLimite(PAGINA);
  }, [query, facultyId]);

  /*
   * Sin `useMemo`, y a propósito.
   *
   * El catálogo no es una prop: es el REGISTRO en memoria de `@clubap/shared`, que otro
   * sitio vuelve a llenar cuando se aprueba un club o cambia un logo. Memorizarlo por
   * `facultyId` congelaría la lista en la que había al montar, y el club recién fundado
   * no aparecería hasta cerrar y volver a abrir — un bug que se ve como "el directorio
   * miente" y no como "falta una dependencia". Derivar en cada render es un `sort` y un
   * `filter` sobre unos cientos de entradas: más barato que el fallo que evita.
   */
  const delSitio: ClubDef[] = facultyId === null ? activeClubs() : clubsOfFaculty(facultyId);

  const busca = plano(query.trim());
  const filtrados = busca
    ? delSitio.filter(
        (club) => plano(club.code).includes(busca) || plano(club.name).includes(busca),
      )
    : delSitio;

  const visibles = filtrados.slice(0, limite);
  const faltan = filtrados.length - visibles.length;
  const facultad = FACULTIES.find((entry) => entry.id === facultyId);

  return (
    <div className="overlay">
      <div className="panel sheet club-sheet">
        <div className="sheet__head">
          <h2 className="sheet__title">{facultad ? `Clubes de ${facultad.code}` : "Clubes"}</h2>
          <span className="sheet__coins">{delSitio.length}</span>
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        <div className="club-buscador">
          <input
            className="join__input"
            value={query}
            placeholder="Buscar por siglas o nombre…"
            aria-label="Buscar un club por siglas o nombre"
            onChange={(event) => setQuery(event.target.value)}
          />
          {query && (
            <button
              className="social__quiet"
              type="button"
              title="Limpiar la búsqueda"
              onClick={() => setQuery("")}
            >
              ×
            </button>
          )}
        </div>

        {delSitio.length === 0 && (
          <div className="sheet__empty">
            <p>
              {facultad
                ? `Todavía no hay ningún club de ${facultad.code}.`
                : "Todavía no hay ningún club en el campus."}
            </p>
            <p className="sheet__hint">Puedes fundar el primero.</p>
          </div>
        )}

        {delSitio.length > 0 && filtrados.length === 0 && (
          <p className="sheet__hint">Ningún club se llama así. Prueba con menos letras.</p>
        )}

        {visibles.length > 0 && (
          <ul className="sheet__list">
            {visibles.map((club) => {
              const esMio = mine.includes(club.id);

              return (
                <li className={`sheet__item${esMio ? " club-fila--mia" : ""}`} key={club.id}>
                  <span
                    className="badge club-marca"
                    style={{ color: club.color, borderColor: club.color }}
                  >
                    <ClubMark club={club} size={26} />
                  </span>

                  <button
                    className="club-abrir"
                    type="button"
                    title={`Abrir la ficha de ${club.name}`}
                    onClick={() => onOpenClub(club.id)}
                  >
                    <span className="club-siglas">{club.code}</span>
                    <span className="sheet__name">{club.name}</span>
                    {club.blurb && <span className="social__blurb">{club.blurb}</span>}
                  </button>

                  {esMio && (
                    <span className="sheet__badge" title="Estás en este club">
                      tuyo
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {faltan > 0 && (
          <div className="club-acciones">
            <button
              className="btn btn--small"
              type="button"
              onClick={() => setLimite((actual) => actual + PAGINA)}
            >
              Ver {faltan > PAGINA ? PAGINA : faltan} más
            </button>
            <span className="sheet__hint">
              {faltan} sin mostrar. Búscalos por sus siglas si sabes cuál quieres.
            </span>
          </div>
        )}

        <div className="club-acciones">
          <button className="btn" type="button" onClick={onFound}>
            Fundar un club
          </button>
        </div>

        <p className="sheet__hint">
          {facultad
            ? "Los clubes de esta facultad tienen su sede en este edificio. Los transversales están en el patio de contenedores."
            : "Cada club tiene su contenedor. Entra a la ficha para ver quién está y qué se ha convocado."}
        </p>
      </div>
    </div>
  );
}
